import React from 'react'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import CheckIcon from '@mui/icons-material/Check'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'
import {
  DELIVERY_STATUS_PILL, deviationColor, deviationText, expectedEndText, isClassified, requiredDateText
} from './deliveryStatusPresentation'

// RN29 -- the icon of each delivery pill; "No date" has none.
export const PILL_ICON = {
  LATE: <AccessTimeIcon />,
  ON_TIME: <CheckIcon />,
  EARLY: <ArrowUpwardIcon />
}

/**
 * Story 175 (S114) -- the delivery pill of a fabrication order, from the `deliveryStatus` Sun sent (D-3:
 * delivery, not life cycle). Nothing is painted for a status Jupiter does not know.
 */
export const DeliveryStatusPill = ({ deliveryStatus, ...chipProps }) => {
  const { t_ } = useSmartTranslation()
  const pill = DELIVERY_STATUS_PILL[deliveryStatus]
  if (!pill) return null
  return (
    <Chip
      size='small'
      color={pill.color}
      icon={PILL_ICON[deliveryStatus]}
      label={systemValueLabel(t_, 'DeliveryStatus', deliveryStatus)}
      data-status={deliveryStatus}
      {...chipProps}
    />
  )
}

const GreyText = ({ children }) => (
  <Typography variant='body2' component='span' color='text.secondary'>{children}</Typography>
)

// Contract §7 / DL-1 (b) -- the cell of the `deliveryDate` column, relabelled "Required date".
export const RequiredDateCell = ({ row }) => {
  const { t_ } = useSmartTranslation()
  return <GreyText>{requiredDateText(row, t_)}</GreyText>
}

export const ExpectedEndCell = ({ row }) => {
  const { t_ } = useSmartTranslation()
  return <GreyText>{expectedEndText(row, t_)}</GreyText>
}

export const DeliveryDeviationCell = ({ row }) => {
  const { t_ } = useSmartTranslation()
  return (
    <Typography variant='body2' component='span' sx={{ fontWeight: 600 }} color={deviationColor(row.deliveryStatus)}>
      {deviationText(row, t_)}
    </Typography>
  )
}

export const DeliveryStatusCell = ({ row }) => (isClassified(row) ? <DeliveryStatusPill deliveryStatus={row.deliveryStatus} /> : null)

// S116 / RN31 -- the existing FO column, with the amber "Forecast" chip when `fabricationOrderForecast`.
export const FabricationOrderCell = ({ row }) => {
  const { t_ } = useSmartTranslation()
  return (
    <>
      {row.fabricationOrder}
      {row.fabricationOrderForecast === true
        ? <Chip size='small' color='warning' variant='outlined' label={t_('Forecast')} sx={{ marginLeft: 1 }} />
        : null}
    </>
  )
}
